import { useNavigate } from 'react-router'
import { Button } from '../../components/Button'
import { Container } from '../../components/Container'
import { Divider } from '../../components/Divider'
import { NextStepBanner } from '../../components/NextStepBanner'
import { StatusBadge, type BadgeStatus } from '../../components/StatusBadge'
import { Text } from '../../components/Text'
import { useI18n } from '../../i18n'

/**
 * Application Status card — the main component on the status screen. Built in two
 * parts; this is the *heading part*: the application info + status badge, the
 * loan name + cost, and the divider that separates this header from the body
 * (the detail list, added in part 2 into this same Container).
 *
 * Intentionally "a little dynamic": values arrive as display-ready strings (this
 * is a design port — no Intl formatting layer), and `status` drives the badge.
 */

export type ApplicationStatusCardProps = {
  status: BadgeStatus
  /** The caller's asset id from /me/asset-data, e.g. "18543". */
  assetId: string
  /** Loan product name, e.g. "Business Recoverable Grant". */
  loanName: string
  /** Pre-formatted amount, e.g. "$50,000.00". */
  cost: string
  /** Status shown (bare) beside the "Next Steps" label — independent of the
   *  header `status`, e.g. "actionRequired". */
  nextStepStatus: BadgeStatus
  /** What the applicant should do next, e.g. "Upload key documents". */
  nextStep: string
  /** Action button label — a free string (e.g. "View Details" / "View Payment
   *  Details"); intentionally not enumerated since the wording is undecided. */
  actionLabel: string
  /** Route the "View Details" button navigates to (e.g. the current step's
   *  destination). `onAction`, if provided, overrides this; if neither is set the
   *  button is inert. */
  viewDetailsTo?: string
  /** Invoked when the action button is pressed. Optional; overrides `viewDetailsTo`. */
  onAction?: () => void
}

export function ApplicationStatusCard({
  status,
  assetId,
  loanName,
  cost,
  nextStepStatus,
  nextStep,
  actionLabel,
  viewDetailsTo,
  onAction,
}: ApplicationStatusCardProps) {
  const { t } = useI18n()
  const navigate = useNavigate()
  const handleAction =
    onAction ?? (viewDetailsTo ? () => navigate(viewDetailsTo) : undefined)

  return (
    // `flex` overrides Container's base `block` via tailwind-merge.
    <Container className="flex flex-col gap-4 p-4">
      {/* 1. Asset id + status badge. No submission date — the BFF doesn't
          return one — so the asset id carries the row on its own. */}
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 flex-col gap-0.5">
          <Text as="p" variant="caption" className="text-gray-600">
            {t('home.assetId')}
          </Text>
          <Text
            as="p"
            variant="base"
            weight="semibold"
            mono
            className="truncate text-gray-900"
          >
            {assetId}
          </Text>
        </div>
        <StatusBadge status={status} />
      </div>

      {/* 2. Loan name + cost */}
      <div className="flex flex-col gap-1">
        <Text as="h3" variant="lg" weight="bold" className="text-gray-900">
          {loanName}
        </Text>
        <Text as="p" variant="base" weight="semibold" className="text-gray-900">
          {cost}
        </Text>
      </div>

      {/* 3. List divider — separates the header from the body below. */}
      <Divider />

      {/* 4. Next steps — static label + dynamic (bare) status badge */}
      <div className="flex items-center justify-between gap-3">
        <Text as="p" variant="caption" weight="semibold" className="text-gray-700">
          {t('applicationStatus.card.nextSteps')}
        </Text>
        <StatusBadge status={nextStepStatus} container={false} />
      </div>

      {/* 5. Reusable banner detailing what the next step is */}
      <NextStepBanner>{nextStep}</NextStepBanner>

      {/* 6. Card action — outline (non-primary) button; label is a free string. */}
      <Divider />
      <Button intent="outline" fullWidth onClick={handleAction}>
        {actionLabel}
      </Button>
    </Container>
  )
}
